//Check the user's current app version vs. the most up-to-date one
import React, { useEffect, useState } from 'react';
import {
  SafeAreaView,
  View,
  ScrollView,
  Image,
  Text,
  Pressable,
  AppState,
  Alert,
  Platform,
  ViewStyle,
  TextStyle,
} from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import metadata from '../build-metadata.json';

const UseVersionCheck = () => {
  const [appState, setAppState] = useState(AppState.currentState);
  //const VERSION_CHECK_URL = `${process.env.REACT_NATIVE_API_SERVER}/build-metadata.json`
  const VERSION_CHECK_URL = `https://stage.meanderev.co/build-metadata.json`;

  let currentVersion: string;
  if (metadata.buildBranch === 'dev') {
    currentVersion = `${metadata.buildMajor}.${metadata.buildMinor}.${metadata.buildRevision}.${metadata.buildBranch}-${metadata.devBuild}`;
  } else if (metadata.buildBranch === 'stage') {
    currentVersion = `${metadata.buildMajor}.${metadata.buildMinor}.${metadata.buildRevision}.${metadata.buildBranch}-${metadata.stageBuild}`;
  } else if (metadata.buildBranch === 'production') {
    currentVersion = `${metadata.buildMajor}.${metadata.buildMinor}.${metadata.buildRevision}`;
  } else {
    currentVersion = 'dev';
  }

  const isVersionContained = (
    d: {
      buildMajor: number;
      buildMinor: number;
      buildRevision: number;
      buildBranch: string;
      devBuild?: number;
      stageBuild?: number;
    },
    currentVersion: string
  ): boolean => {
    // For dev branch, use the existing logic
    if (metadata.buildBranch === 'dev') {
      return currentVersion.includes('dev');
    }

    // Extract current version components from the currentVersion string
    const currentVersionMatch = currentVersion.match(/(\d+)\.(\d+)\.(\d+)/);
    if (!currentVersionMatch) {
      // If we can't parse the current version, fall back to the old behavior
      let expectedVersion: string;
      if (metadata.buildBranch === 'stage') {
        expectedVersion = `${d.buildMajor}.${d.buildMinor}.${d.buildRevision}.${d.buildBranch}-${d.stageBuild}`;
      } else if (metadata.buildBranch === 'production') {
        expectedVersion = `${d.buildMajor}.${d.buildMinor}.${d.buildRevision}`;
      } else {
        expectedVersion = `${d.buildMajor}.${d.buildMinor}.${d.buildRevision}`;
      }
      return currentVersion.includes(expectedVersion);
    }

    const currentMajor = parseInt(currentVersionMatch[1]);
    const currentMinor = parseInt(currentVersionMatch[2]);
    const currentRevision = parseInt(currentVersionMatch[3]);

    // Compare version numbers
    if (d.buildMajor > currentMajor) {
      return false; // Expected version is newer (higher major)
    } else if (d.buildMajor < currentMajor) {
      return true; // Expected version is older (lower major)
    } else {
      // Same major version, compare minor
      if (d.buildMinor > currentMinor) {
        return false; // Expected version is newer (higher minor)
      } else if (d.buildMinor < currentMinor) {
        return true; // Expected version is older (lower minor)
      } else {
        // Same major and minor, compare revision
        if (d.buildRevision > currentRevision) {
          return false; // Expected version is newer (higher revision)
        } else {
          return true; // Expected version is same or older
        }
      }
    }
  };

  const checkForUpdate = async () => {
    try {
      const response = await fetch(VERSION_CHECK_URL);
      const data = await response.json();
      // console.log('updateData found: ');
      // console.log(data);

      if (!isVersionContained(data, currentVersion)) {
        if (
          Platform.OS === 'web' &&
          process.env.REACT_NATIVE_ENV !== 'development'
        ) {
          // Use browser alert for React Native Web
          if (
            window.confirm(
              `A new version (${data.buildMajor}.${data.buildMinor}.${data.buildRevision}) is available. Reload now?`
            )
          ) {
            window.location.reload();
          }
        } else {
          // Use React Native's Alert for mobile apps
          // Alert.alert(
          //     'Update Available',
          //     `A new version (${data.buildMajor}.${data.buildMinor}.${data.buildRevision}) is available. Please reload the app.`,
          //     [{ text: 'Reload', onPress: () => window.location.reload() }]
          // );
        }
      }
    } catch (error) {
      console.error('Error checking version:', error);
    }
  };

  useEffect(() => {
    // Run checkForUpdate immediately on mount
    checkForUpdate();

    // Listen for app state changes
    const subscription = AppState.addEventListener('change', (nextAppState) => {
      if (appState.match(/inactive|background/) && nextAppState === 'active') {
        checkForUpdate();
      }
      setAppState(nextAppState);
    });

    return () => {
      subscription.remove();
    };
  }, []);

  return (
    <View style={styles.versionContainer}>
      <Text style={styles.versionText}>Version {currentVersion}</Text>
    </View>
  );
};

const styles = {
  versionContainer: {
    position: 'absolute' as ViewStyle['position'],
    width: '100%' as ViewStyle['width'],
    bottom: 1,
    alignItems: 'center' as ViewStyle['alignItems'],
    justifyContent: 'center' as ViewStyle['justifyContent'],
    paddingVertical: 1,
    paddingBottom: 0,
  },
  versionText: {
    fontSize: 12,
    color: 'rgba(255, 255, 255, 0.6)',
    textAlign: 'center' as TextStyle['textAlign'],
  },
};

export default UseVersionCheck;
